import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AccessibilityProvider } from "@/context/AccessibilityContext";
import { GeoProvider } from "@/context/GeoContext";
import { OakPadProvider } from "@/context/OakPadContext";
import Landing from "@/pages/Landing";
import Hub from "@/pages/Hub";
import LearnLanguage from "@/pages/LearnLanguage";
import Dashboard from "@/pages/Dashboard";
import Exam from "@/pages/Exam";
import PathView from "@/pages/PathView";
import LibraryView from "@/pages/LibraryView";
import Library from "@/pages/Library";
import Planner from "@/pages/Planner";
import Referrals from "@/pages/Referrals";
import Certificate from "@/pages/Certificate";
import CertificatePublic from "@/pages/CertificatePublic";
import PublicNote from "@/pages/PublicNote";
import Privacy from "@/pages/Privacy";
import Refund from "@/pages/Refund";
import Terms from "@/pages/Terms";
import IssuerPage from "@/pages/IssuerPage";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import AdminRevenue from "@/pages/AdminRevenue";
import LabInstructor from "@/pages/LabInstructor";
import LabMeshInstructor from "@/pages/LabMeshInstructor";
import AuthCallback from "@/pages/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoadingScreen from "@/components/LoadingScreen";
import DailyLimitGate from "@/components/DailyLimitGate";
import TrustGates from "@/components/TrustGates";
import OakPad from "@/components/OakPad";
import GuideChat from "@/components/GuideChat";
import { useOakPad } from "@/context/OakPadContext";
import WelcomeGate from "@/components/WelcomeGate";
import PostHogBridge from "@/components/PostHogBridge";
import ReferralRedeemBridge from "@/components/ReferralRedeemBridge";
import ResumePathBridge from "@/components/ResumePathBridge";
import LevelUpModal from "@/components/LevelUpModal";
import FeedbackButton from "@/components/FeedbackButton";
import PublicPreview from "@/pages/PublicPreview";
import Explore from "@/pages/Explore";
import "@/App.css";

/**
 * Root gate — only evaluated AFTER session hydration is finished.
 *   user present → Hub
 *   user absent  → Landing (the marketing/sign-in page lives on both `/`
 *                  for guests and `/login` for explicit redirects)
 */
function RootGate() {
  const { user } = useAuth();
  return user ? <Hub /> : <Landing />;
}

// OAK-Pad lives ONLY on the course-reading surfaces — `/academy` (Dashboard
// renders BentoPath + ExplainDeeperSheet) and `/path/:pathId` (PathView).
// It's a floating FAB widget the learner pops open while reading their
// generated course; highlights auto-import via HighlightToClip, notes
// auto-save via OakPadContext, and the drawer offers PDF export + native
// share. Hidden on Hub (`/`), Library, Settings, Admin, public pages, etc.
// Iter 54 — refined the iter53 visibility rules per user feedback:
//   • OakPad ("Publish your study" / note-taking FAB):
//       hidden on /academy (dashboard — browsing paths, not reading)
//       VISIBLE on /path/:pathId (the course reading pane — note-taking
//       is the whole point here)
//   • OAK Guide (help chat):
//       VISIBLE on /academy (users need help while exploring)
//       hidden on /path/:pathId (no clutter while deep-reading)
function _isAcademyPage(pathname) {
  const p = pathname || "";
  return p === "/academy" || p.startsWith("/academy/");
}
function _isCourseReadingPane(pathname) {
  return (pathname || "").startsWith("/path/");
}

function OakPadGate() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  const path = location.pathname || "";
  // Hide on /academy (clutter on the path-browsing dashboard).
  // Show on /path/:pathId so learners can take notes while reading.
  if (_isAcademyPage(path)) return null;
  if (!_isCourseReadingPane(path)) return null;
  return <OakPad />;
}

const GUIDE_HIDDEN_PREFIXES = ["/cert/", "/notes/", "/privacy", "/terms", "/refund"];

// Iter82 — the learner-focused surfaces where any floating widget
// (Guide chat, Feedback pill) actively *interferes* with the lesson UI
// and was reported by the user. Course reading pane (/path/) and the
// new gamified Language Learning hub (/learn-language).
function _isLearningSurface(pathname) {
  const p = pathname || "";
  return p.startsWith("/path/") || p === "/learn-language" || p.startsWith("/learn-language/");
}

function GuideChatGate() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  const path = location.pathname || "";
  // Iter56: also show on Hub (`/`) per user request — they want the
  // help chat available everywhere except where it'd clutter (course
  // reading pane) or confuse (login screen, public preview pages).
  if (path === "/login") return null;
  // Iter82: hide on every learning surface (course pane + language
  // lesson hub) so the floating shortcut doesn't overlap exercises.
  if (_isLearningSurface(path)) return null;
  if (GUIDE_HIDDEN_PREFIXES.some((p) => path.startsWith(p))) return null;
  return <GuideChat />;
}

/**
 * FeedbackGate — Iter77. Render the floating "Feedback" pill only when
 * the user is authenticated. Hide on /admin (admin has its own inbox)
 * and on auth callback routes so it doesn't overlap OAuth UI.
 * Iter82 — also hide on learning surfaces (course pane + language hub)
 * where the user reported the pill blocks the lesson chrome.
 */
function FeedbackGate() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  const path = location.pathname || "";
  if (path.startsWith("/admin") || path === "/login") return null;
  if (_isLearningSurface(path)) return null;
  return <FeedbackButton floating />;
}


function AppRouter() {
  const location = useLocation();
  const { initialSessionFetched } = useAuth();

  // 1) Always intercept the OAuth fragment BEFORE any route evaluation — even
  //    before the session-hydration gate. AuthCallback owns the flip.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }

  // 2) The strict hydration gate: while we haven't yet resolved /auth/me
  //    for the very first time, show a full-screen spinner. Do NOT evaluate
  //    ProtectedRoute, do NOT redirect to /login — those decisions are only
  //    allowed once `initialSessionFetched === true`.
  if (!initialSessionFetched) {
    return <LoadingScreen label="Warming up OAK…" />;
  }

  // Capture ?ref=XYZ → store locally so signup can attribute later
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    // Accept 4-16 chars, alphanumeric + - and _ (matches both legacy
    // uppercase codes and the newer secrets.token_urlsafe-generated ones).
    if (ref && /^[A-Za-z0-9_-]{4,16}$/.test(ref)) {
      try { localStorage.setItem("oak_ref", ref); } catch {}
    }
    // Capture ?resume_path=PID — set when a guest clicks "Unlock" on a
    // public preview `/p/{slug}` page. ResumePathBridge picks this up
    // post-auth and deep-links the user into `/path/{pid}` so they
    // don't have to navigate manually after Google sign-in.
    const rp = params.get("resume_path");
    if (rp && /^[A-Za-z0-9_-]{6,64}$/.test(rp)) {
      try { localStorage.setItem("oak_resume_path", rp); } catch {}
    }
  }

  return (
    <Routes>
      <Route path="/" element={<RootGate />} />
      <Route path="/login" element={<Landing />} />
      <Route path="/academy" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/path/:pathId" element={<ProtectedRoute><PathView /></ProtectedRoute>} />
      <Route path="/exam/:examId" element={<ProtectedRoute><Exam /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
      <Route path="/library/:itemId" element={<ProtectedRoute><LibraryView /></ProtectedRoute>} />
      <Route path="/planner" element={<ProtectedRoute><Planner /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
      <Route path="/certificate/:pathId" element={<ProtectedRoute><Certificate /></ProtectedRoute>} />
      <Route path="/cert/:certId" element={<CertificatePublic />} />
      <Route path="/notes/:slug" element={<PublicNote />} />
      <Route path="/p/:slug" element={<PublicPreview />} />
      <Route path="/explore" element={<Explore />} />
      <Route path="/learn-language" element={<ProtectedRoute><LearnLanguage /></ProtectedRoute>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refund" element={<Refund />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/issuer" element={<IssuerPage />} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/admin/revenue" element={<ProtectedRoute><AdminRevenue /></ProtectedRoute>} />
      <Route path="/lab/instructor" element={<LabInstructor />} />
      <Route path="/lab/mesh-instructor" element={<LabMeshInstructor />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AccessibilityProvider>
        <GeoProvider>
          <AuthProvider>
            <OakPadProvider>
              <div className="oak-sunset" />
              <AppRouter />
              <DailyLimitGate />
              <TrustGates />
              <WelcomeGate />
              <PostHogBridge />
              <ReferralRedeemBridge />
              <ResumePathBridge />
              <OakPadGate />
              <GuideChatGate />
              <LevelUpModal />
              <FeedbackGate />
              <Toaster theme="dark" position="top-right" richColors />
            </OakPadProvider>
          </AuthProvider>
        </GeoProvider>
      </AccessibilityProvider>
    </BrowserRouter>
  );
}
